import { VoiceBrowserDialog } from "@/components/scenarios/VoiceBrowserDialog";
import { isByoCompatibleVoice, filterVoices, voiceFilterChoices, type VoiceFilters } from "@/lib/scenarios/providers/liveavatar/voiceSelection";
import { languageName } from "@/lib/liveavatar/options";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PickerSelect } from "./PickerSelect";
import {
  DID_BACKGROUND_TILES,
  DID_BACKGROUND_CUSTOM,
  DID_BACKGROUND_NONE,
  didBackgroundPreview,
} from "@/lib/scenarios/providers/did/backgrounds";
import { did } from "@/lib/scenarios/providers/did/client";
import { AvatarPicker } from "./AvatarPicker";
import { BackgroundPicker } from "./BackgroundPicker";
import { InteractionModePicker } from "./InteractionModePicker";
import { ProviderPicker } from "./ProviderPicker";
import { useProviderPickers, DEFAULT_VOICE_ID } from "@/lib/scenarios/providers/pickers";
import { shouldReconcileVoice } from "@/lib/scenarios/providers/liveavatar/voiceSelection";
import { getAdapter, getAdapterByProvider } from "@/lib/scenarios/providers";
import { inputsOf, interactionModeOf } from "@/lib/scenarios/providers/spec";
import { providersForMode } from "@/lib/scenarios/providers/modeSupport";
import { questionnaireIdsOf, roleplayIdsOf } from "@/lib/scenarios/questionnaireIds";
import { SourceInput, type SourceInputValue } from "./SourceInput";
import { persistedToDraft, type SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import { syncKnowledgeSources as syncKalturaKnowledgeSources } from "@/lib/scenarios/providers/kaltura/knowledgeDocuments";
import { QuestionListEditor } from "./QuestionListEditor";
import { LearnerDisplayPicker, type DisplayFormValue } from "./LearnerDisplayPicker";
import { BookMeetingPreviewModal } from "./BookMeetingPreviewModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buildContextPrompt, selectedRolePlayScenario } from "@/lib/prompts";
import { getMode, type InteractionMode } from "@/lib/scenarios/interactionModes";
import { DEFAULT_MCQ_INSTRUCTIONS } from "@/lib/scenarios/interactionModes";
import { generateScenarioField } from "@/lib/scenarios/generateClient";
import type {
  Scenario,
  ScenarioContextBlock,
  CreateScenarioInput,
  PublishMode,
  CtaType,
  MCQQuestion,
  MCQConfig,
} from "@/lib/powtoon-be/types";
import type { SaveScenarioMeta } from "@/lib/scenarios/saveScenario";
import { resolveDisplayForPersist } from "@/lib/scenarios/display";
import { getCachedUrlEmbedResult, isDisplayableVerdict } from "@/lib/scenarios/urlEmbedCheck";
import {
  DEFAULT_GENERATE_MCQ_COUNT,
  DEFAULT_PASS_RATE,
  serializeQuestions,
  normalizeIncoming,
  newQuestionnaireId,
} from "@/lib/scenarios/mcq";
import { RolePlayEditor } from "@/lib/scenarios/modes/role_play/RolePlayEditor";
import {
  defaultRolePlayConfig,
  isRolePlayConfigEmpty,
  normalizeRolePlayConfig,
} from "@/lib/scenarios/modes/role_play/schema";
import { validateRolePlay } from "@/lib/scenarios/modes/role_play/validate";
import type { RolePlayConfig } from "@/lib/powtoon-be/types";
import type {
  TavusKnowledgeDocument,
  DidKnowledgeDocument,
  KalturaKnowledgeSource,
  ScenarioSourceItem,
} from "@/lib/powtoon-be/types";
import {
  isTerminalDocumentStatus,
  refreshDocumentStatus,
  registerSourceDocument,
  verifyDocuments,
  deleteTavusDocuments,
  getPresentationSkillState,
  attachPresentationSkill,
} from "@/lib/scenarios/providers/tavus/knowledgeDocuments";
import { getInternetSearchSkillState } from "@/lib/scenarios/providers/tavus/internetSearchSkill";
import {
  isTerminalDocumentStatus as isTerminalDidDocumentStatus,
  refreshDocumentStatus as refreshDidDocumentStatus,
  registerSourceDocument as registerDidSourceDocument,
  verifyDocuments as verifyDidDocuments,
  deleteDidDocuments,
} from "@/lib/scenarios/providers/did/knowledgeDocuments";
import { toast } from "sonner";
import { ChevronRight, Loader2, Sparkles } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import {
  liveAvatarModeForOrg,
  preferImportedVoices,
  isImportedVoicesOnlyOrg as isImportedVoicesOnlyOrgFn,
  canUseUpsellPublishMode,
  canUseUserAvatars,
  getAvailableProviders,
  type ProviderId,
} from "@/lib/auth/orgSettings";

function defaultBlock(): ScenarioContextBlock {
  return {
    interaction_mode: "conversational",
    persona: "",
    knowledge: "",
    instructions: "",
    opening_text: "",
    generated_prompt: "",
  };
}

/** Read a picked file as a base64 data URL for the D-ID upload proxy. */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read this file."));
    reader.readAsDataURL(file);
  });
}

function GeneratingOverlay({ label }: { label: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/70 backdrop-blur-sm pointer-events-none z-10">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{label}</span>
      </div>
    </div>
  );
}

function FieldGenerateButton({
  value,
  generating,
  onClick,
}: {
  value: string;
  generating: boolean;
  onClick: () => void;
}) {
  const hasText = value.trim().length > 0;
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onClick}
      disabled={generating}
      className="absolute bottom-2 right-2 z-20 h-7 px-2.5 text-xs shadow-sm"
    >
      <Sparkles className="h-3 w-3 mr-1" />
      {generating ? "…" : hasText ? "Enhance my text" : "Generate for me"}
    </Button>
  );
}

function TextareaWrap({
  generating,
  generatingLabel,
  children,
  action,
}: {
  generating: boolean;
  generatingLabel: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="relative">
      {children}
      {action}
      {generating && <GeneratingOverlay label={generatingLabel} />}
    </div>
  );
}

type Errors = Partial<
  Record<
    | "name"
    | "description"
    | "knowledge"
    | "persona"
    | "instructions"
    | "avatarId"
    | "voiceId"
    | "language"
    | "faceId"
    | "ctaUrl"
    | "redirectUrl"
    | "mcq"
    | "mcqPassingRate"
    | "display",
    string
  >
>;

function Req() {
  return <span className="text-destructive ml-0.5">*</span>;
}
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}

export function ScenarioForm({
  initial,
  initialQuestionnaire,
  initialRoleplay,
  onSubmit,
  onCancel,
  submitLabel = "Save",
}: {
  initial?: Scenario;
  /** Most-recent questionnaire artifact content for this scenario, if any. */
  initialQuestionnaire?: MCQConfig | null;
  /**
   * Most-recent `live_avatar_roleplay` artifact content for this scenario.
   * Takes precedence over the inline `role_play` block when present.
   */
  initialRoleplay?: RolePlayConfig | null;
  onSubmit: (data: CreateScenarioInput, meta: SaveScenarioMeta) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");

  const initBlock = initial?.content?.context_block ?? defaultBlock();
  // Hydrate provider-specific fields through the adapter. Adapters are
  // the single source of truth for both read (`toFormState`) and write
  // (`toContent`) shapes — so switching providers only requires updating
  // the adapter, not this form.
  const initialProvider: ProviderId = initial?.content?.provider ?? "liveavatar";
  const seed = initial ? getAdapter(initial).toFormState(initial) : {};
  // Source inputs live on whichever block this provider owns — resolved
  // through the provider spec registry rather than branching here.
  const seedInputs = initial ? inputsOf(initial.content) : [];

  // Interaction mode is provider-aware; adapter `toFormState` already
  // returns the right one, with the registry as the fallback.
  const [mode, setMode] = useState<InteractionMode>(
    (seed.mode as InteractionMode | undefined) ?? interactionModeOf(initial?.content),
  );
  const [sources, setSources] = useState<SourceInputValue>(() => seedInputs.map(persistedToDraft));
  const initialInputsRef = useRef(seedInputs);
  const [knowledge, setKnowledge] = useState(seed.knowledge ?? "");
  const [persona, setPersona] = useState(seed.persona ?? "");
  const [instructions, setInstructions] = useState(
    seed.instructions ||
      (((seed.mode as InteractionMode | undefined) ?? interactionModeOf(initial?.content)) ===
        "multiple_choice" && (initialQuestionnaire?.questions?.length ?? 0) > 0
        ? DEFAULT_MCQ_INSTRUCTIONS
        : ""),
  );
  const [openingText, setOpeningText] = useState(seed.openingText ?? "");

  const [mcqQuestions, setMcqQuestions] = useState<MCQQuestion[]>(() =>
    normalizeIncoming(initialQuestionnaire?.questions),
  );
  const [mcqOpts, setMcqOpts] = useState<{ count: number; passRate: number }>(() => ({
    // count is transient — used as the "generate N questions" target only;
    // it is never persisted on the questionnaire.
    count: DEFAULT_GENERATE_MCQ_COUNT,
    passRate: initialQuestionnaire?.pass_rate ?? DEFAULT_PASS_RATE,
  }));
  const initialQuestionnaireIdRef = useRef<string>(
    initialQuestionnaire?.id || newQuestionnaireId(),
  );
  const initialQuestionnaireRef = useRef<MCQConfig | null>(initialQuestionnaire ?? null);

  // The `live_avatar_roleplay` artifact is the only copy of the role-play
  // content — the provider blocks carry ids only.
  const [roleplay, setRoleplay] = useState<RolePlayConfig>(() =>
    normalizeRolePlayConfig(initialRoleplay ?? null),
  );
  // Value the form loaded — save uses it as the dirty check for the artifact.
  const initialRoleplayRef = useRef<RolePlayConfig | null>(initialRoleplay ?? null);

  // Provider-agnostic avatar/voice/language state. Adapter `toContent`
  // maps `avatarId` → `pal.face_id` for Tavus and → `avatar_id` for LA;
  // an empty `voiceId` under Tavus means "use face default".
  const [avatarId, setAvatarId] = useState(seed.avatarId ?? "");
  const [voiceId, setVoiceId] = useState(seed.voiceId ?? "");
  const [language, setLanguage] = useState(seed.language ?? "");
  const [provider, setProvider] = useState<ProviderId>(initialProvider);
  // Provider is a creation-time decision: an existing scenario is already
  // provisioned upstream (pal / agent / knowledge base) for its provider.
  const isEditing = !!initial?.id;
  const isTavus = provider === "tavus";
  const isKaltura = provider === "kaltura";
  const isDid = provider === "did";

  // D-ID-only: background baked onto the presenter. Greenscreen avatars
  // composite onto it; "none" leaves the avatar as the catalog ships it.
  const [backgroundId, setBackgroundId] = useState(seed.backgroundId ?? "none");
  // Hosted URL of a trainer-uploaded background (D-ID stores the image).
  const [backgroundUrl, setBackgroundUrl] = useState(seed.backgroundUrl ?? "");

  const [publishMode, setPublishMode] = useState<PublishMode>(
    initial?.content?.publish_mode ?? "hidden",
  );
  const [autoStart, setAutoStart] = useState<boolean>(initial?.content?.auto_start ?? false);
  const [ctaType, setCtaType] = useState<CtaType>(initial?.content?.cta_type ?? "none");
  const [ctaUrl, setCtaUrl] = useState(initial?.content?.cta_url ?? "");
  const [redirectUrl, setRedirectUrl] = useState(initial?.content?.redirect_url ?? "");


  // Tavus-only session control knobs. Persisted on `pal`; read here via
  // `seed` so the round-trip stays in the adapter.
  const [disableCameraSharing, setDisableCameraSharing] = useState<boolean>(
    seed.disableCameraSharing ?? false,
  );
  const [disableScreenSharing, setDisableScreenSharing] = useState<boolean>(
    seed.disableScreenSharing ?? false,
  );
  // Tavus-only: scenario source registered with the Tavus knowledge base and
  // retrieved (RAG) during the conversation. "" = none.
  const [knowledgeDocSourceKey, setKnowledgeDocSourceKey] = useState<string>(
    seed.knowledgeDocumentSourceKey ?? "",
  );
  const [presentationEnabled, setPresentationEnabled] = useState<boolean>(
    seed.presentationEnabled ?? false,
  );
  // Tavus-only: `internet_search` skill. Seeded from the persisted value, then
  // replaced by whatever Tavus actually reports for the pal (see below).
  const [internetSearchEnabled, setInternetSearchEnabled] = useState<boolean>(
    seed.internetSearchEnabled ?? false,
  );
  // D-ID-only: the scenario's own knowledge base and the documents inside it.
  // The base id is minted upstream on the first registration.
  const [didKnowledgeId, setDidKnowledgeId] = useState<string>(seed.didKnowledgeId ?? "");
  const [didDocEntries, setDidDocEntries] = useState<DidKnowledgeDocument[]>(
    seed.didKnowledgeDocuments ?? [],
  );
  const [didGrounded, setDidGrounded] = useState<boolean>(seed.didKnowledgeGrounded ?? false);
  // D-ID-only: let the avatar call the `present_document` client tool.
  const [didPresentDocument, setDidPresentDocument] = useState<boolean>(
    seed.didPresentDocument ?? false,
  );
  const [didRegisteringKey, setDidRegisteringKey] = useState<string | null>(null);

  // Kaltura-only: the scenario's knowledge record. Record-level, not
  // per-document — a sync rewrites the whole web-source list, so the editor
  // tracks only which source keys were in the last successful sync.
  const [kalturaKnowledgeId, setKalturaKnowledgeId] = useState<number | null>(
    seed.kalturaKnowledgeId ?? null,
  );
  const [kalturaSyncedKeys, setKalturaSyncedKeys] = useState<string[]>(
    seed.kalturaKnowledgeSourceKeys ?? [],
  );
  const [kalturaKnowledgeSources, setKalturaKnowledgeSources] = useState<KalturaKnowledgeSource[]>(
    seed.kalturaKnowledgeSources ?? [],
  );
  const [kalturaSyncedAt, setKalturaSyncedAt] = useState<string>(
    seed.kalturaKnowledgeSyncedAt ?? "",
  );
  const [kalturaWebSearch, setKalturaWebSearch] = useState<boolean>(
    seed.kalturaWebSearch ?? false,
  );
  const [kalturaDisableScreenShare, setKalturaDisableScreenShare] = useState<boolean>(
    seed.kalturaDisableScreenShare ?? false,
  );
  const [kalturaPresentDocument, setKalturaPresentDocument] = useState<boolean>(
    seed.kalturaPresentDocument ?? false,
  );
  const [kalturaSyncing, setKalturaSyncing] = useState(false);

  const [forceByoLivekit, setForceByoLivekit] = useState<boolean>(
    initial?.content?.force_byo_livekit === true,
  );
  const [showAiNotice, setShowAiNotice] = useState<boolean>(
    initial?.content?.show_ai_notice === true,
  );
  const [display, setDisplay] = useState<DisplayFormValue | null>(() => {
    const d = initial?.content?.display;
    if (!d) return null;
    const avatarPosition = d.avatar_position ?? "right";
    if (!d.source) return { layout: d.layout, avatarPosition, selection: undefined };
    if (d.source.kind === "url") {
      return { layout: d.layout, avatarPosition, selection: { kind: "url", url: d.source.url } };
    }
    // Match the upload by object_id against the freshly hydrated drafts.
    const objectId = d.source.object_id;
    const match = sources.find(
      (s): s is Extract<SourceInputDraft, { kind: "upload" }> =>
        s.kind === "upload" && s.object_id === objectId,
    );
    return {
      layout: d.layout,
      avatarPosition,
      selection: match ? { kind: "upload", draftId: match._draftId } : undefined,
    };
  });

  const [errors, setErrors] = useState<Errors>({});
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const { orgDomain } = usePowtoonAuth();
  const isImportedVoicesOnlyOrg = isImportedVoicesOnlyOrgFn(orgDomain);
  const allowUpsellPublishMode = canUseUpsellPublishMode(orgDomain);
  const availableProviders = useMemo(
    () => providersForMode(mode, getAvailableProviders(orgDomain)),
    [orgDomain, mode],
  );

  const singleProvider = availableProviders.length <= 1;
  // Unified picker data — one UI for every provider, adapters underneath.
  const pickers = useProviderPickers(provider, {
    avatarId,
    importedVoicesOnly: isImportedVoicesOnlyOrg || (provider === "liveavatar" && forceByoLivekit),
    language,
    includeUserAvatars: canUseUserAvatars(orgDomain),
    importedVoicesFirst: preferImportedVoices(orgDomain),
    selectedVoiceId: provider === "liveavatar" ? voiceId : undefined,
  });

  // Tavus persists "" to mean "use face default"; the picker uses a
  // sentinel option so the dropdown has something to show.
  const displayVoiceId = isTavus ? voiceId || DEFAULT_VOICE_ID : voiceId;
  const [voiceFilters, setVoiceFilters] = useState<VoiceFilters>({});
  const voiceChoices = useMemo(
    () => voiceFilterChoices(pickers.voices.options, { language: voiceFilters.language }),
    [pickers.voices.options, voiceFilters.language],
  );
  const filteredVoiceOptions = useMemo(
    () => filterVoices(pickers.voices.options, voiceFilters, displayVoiceId),
    [pickers.voices.options, voiceFilters, displayVoiceId],
  );
  const hasVoiceFilters = Boolean(voiceFilters.gender || voiceFilters.language || voiceFilters.accent);
  function setDisplayVoiceId(v: string) {
    if (isTavus) setVoiceId(v === DEFAULT_VOICE_ID ? "" : v);
    else setVoiceId(v);
  }

  // Single auto-seed for voice + language once the picker options load.
  // Delegates the provider-specific choice to `pickers.deriveDefaults`.
  useEffect(() => {
    if (provider === "liveavatar" && initial) return;
    if (pickers.avatars.error || pickers.voices.error || pickers.languages.error) return;
    if (pickers.avatars.isLoading || pickers.voices.isLoading || pickers.languages.isLoading)
      return;
    if (!avatarId && !isTavus) return; // LA seeds off the picked avatar
    const needsVoice = !voiceId && !(isTavus && !avatarId);
    const needsLang = !language;
    if (!needsVoice && !needsLang) return;
    if (!isTavus && isImportedVoicesOnlyOrg && needsVoice && !needsLang) return;
    const picked = pickers.avatars.options.find((o) => o.id === avatarId);
    const derived = pickers.deriveDefaults(picked, {
      voices: pickers.voices.options,
      languages: pickers.languages.options,
    });
    if (needsVoice && derived.voiceId !== undefined && derived.voiceId !== "") {
      // Tavus's sentinel means "face default" — keep persisted voiceId empty.
      setVoiceId(derived.voiceId === DEFAULT_VOICE_ID ? "" : derived.voiceId);
    }
    if (needsLang && derived.language !== undefined) {
      setLanguage(derived.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    provider,
    initial,
    avatarId,
    isTavus,
    pickers.avatars.isLoading,
    pickers.voices.isLoading,
    pickers.languages.isLoading,
  ]);

  // If the picked voice is no longer in the filtered list (e.g. the trainer
  // swapped to a different-gender avatar, or changed the language to one this
  // voice cannot speak), swap in the closest valid voice instead of leaving
  // the field empty — the list is already filtered to valid pairs.
  useEffect(() => {
    if (!shouldReconcileVoice(provider)) return;
    if (pickers.voices.isLoading) return;
    if (!voiceId) return;
    if (isTavus && voiceId === "") return;
    const inList = pickers.voices.options.some((o) => o.id === voiceId);
    if (inList) return;
    const next = pickers.voices.options[0]?.id ?? "";
    setVoiceId(next === DEFAULT_VOICE_ID ? "" : next);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, avatarId, language, pickers.voices.isLoading, pickers.voices.options]);


  const [busy, setBusy] = useState(false);
  const [bookPreviewOpen, setBookPreviewOpen] = useState(false);
  const [generatingKnowledge, setGeneratingKnowledge] = useState(false);
  const [generatingPersona, setGeneratingPersona] = useState(false);
  const [generatingOpening, setGeneratingOpening] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  async function handleGenerateField(field: "persona" | "opening_text" | "description") {
    const setBusyFn =
      field === "persona"
        ? setGeneratingPersona
        : field === "opening_text"
          ? setGeneratingOpening
          : setGeneratingDescription;
    const currentValue =
      field === "persona" ? persona : field === "opening_text" ? openingText : description;
    setBusyFn(true);
    try {
      const res = await generateScenarioField({
        field,
        mode,
        knowledge,
        persona,
        currentValue,
        instructions,
        name,
        roleplay:
          field === "opening_text" && mode === "role_play"
            ? (() => {
                const sc = selectedRolePlayScenario(roleplay);
                return {
                  goal: roleplay.goal,
                  avatar_role: sc?.avatar_role,
                  learner_role: sc?.learner_role,
                  emotional_state: sc?.emotional_state,
                  avatar_personality: sc?.avatar_personality,
                  framing_script: sc?.framing_script,
                };
              })()
            : null,
      });
      if (field === "persona") {
        setPersona(res.content);
        clearError("persona");
      } else if (field === "opening_text") {
        setOpeningText(res.content);
      } else {
        setDescription(res.content);
        clearError("description");
      }
      toast.success(currentValue.trim() ? "Text enhanced" : "Generated");
    } catch (e) {
      toast.error(`Generation failed: ${(e as Error).message}`);
    } finally {
      setBusyFn(false);
    }
  }

  const modeMeta = getMode(mode);

  // Uploaded scenario sources that already exist as attachments — the only
  // things Tavus can fetch and turn into a knowledge document.
  const tavusDocOptions = useMemo(
    () =>
      sources
        .filter(
          (s): s is Extract<SourceInputDraft, { kind: "upload" }> =>
            s.kind === "upload" && !!s.object_id,
        )
        .map((s) => ({
          key: `upload:${s.object_id}`,
          label: s.file_name,
          objectId: s.object_id,
        })),
    [sources],
  );
  const pendingTavusDocs = sources.filter((s) => s.kind === "upload" && !s.object_id).length > 0;

  // Tavus documents registered from this scenario's sources. Registration is
  // explicit in the editor (not on save), so the trainer sees the document id
  // and its indexing status before committing the scenario.
  const [docEntries, setDocEntries] = useState<TavusKnowledgeDocument[]>(
    seed.knowledgeDocuments ?? [],
  );
  const [registeringKey, setRegisteringKey] = useState<string | null>(null);

  // Verify the stored pairings against Tavus once, on load: a document deleted
  // upstream comes back as `missing` so its source can be registered again.
  const verifiedRef = useRef(false);
  useEffect(() => {
    if (!isTavus || verifiedRef.current) return;
    const stored = seed.knowledgeDocuments ?? [];
    if (!stored.length) return;
    verifiedRef.current = true;
    let cancelled = false;
    void verifyDocuments(stored).then((fresh) => {
      if (!cancelled) setDocEntries(fresh);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTavus, initial?.id]);

  // Keep polling while Tavus is still indexing — the presentation skill only
  // accepts `ready` documents.
  const pollableIds = docEntries
    .filter((e) => !isTerminalDocumentStatus(e.status) && e.status !== "missing")
    .map((e) => e.document_id)
    .join(",");
  useEffect(() => {
    if (!pollableIds) return;
    let cancelled = false;
    const ids = pollableIds.split(",");
    const timer = window.setInterval(async () => {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            return { id, ...(await refreshDocumentStatus(id)) };
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      setDocEntries((prev) =>
        prev.map((e) => {
          const hit = results.find((r) => r?.id === e.document_id);
          return hit ? { ...e, status: hit.status, error_message: hit.error_message } : e;
        }),
      );
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pollableIds]);

  const selectedDoc = knowledgeDocSourceKey
    ? docEntries.find((e) => e.source_key === knowledgeDocSourceKey)
    : undefined;
  // The document must be `ready` before Tavus accepts the skill, so the switch
  // is locked while it indexes — but the trainer's saved choice is never
  // cleared by that; intent and actual attachment are tracked separately.
  const presentationBlocked = selectedDoc?.status !== "ready";
  const palId = initial?.content?.pal?.pal_id;
  const [skillAttached, setSkillAttached] = useState<boolean | null>(null);
  // Live `internet_search` state: "loading" until Tavus answers, "error" when
  // it can't be read. The switch is locked in both cases so the trainer can
  // never toggle a value we're about to overwrite or can't confirm.
  const [internetSearchProbe, setInternetSearchProbe] = useState<
    "loading" | "ok" | "error" | "none"
  >("none");

  // Reconcile the toggle with the pal: Tavus owns the truth, the stored value
  // is only the starting point. Skipped when there's no pal yet (new scenario).
  useEffect(() => {
    if (!isTavus || mode !== "conversational" || !palId) {
      setInternetSearchProbe("none");
      return;
    }
    let cancelled = false;
    setInternetSearchProbe("loading");
    void getInternetSearchSkillState(palId)
      .then((attached) => {
        if (cancelled) return;
        setInternetSearchEnabled(attached);
        setInternetSearchProbe("ok");
      })
      .catch(() => {
        if (!cancelled) setInternetSearchProbe("error");
      });
    return () => {
      cancelled = true;
    };
  }, [isTavus, mode, palId]);

  // Actual state of the skill on the pal, read once per pal.
  useEffect(() => {
    if (!isTavus || !palId) return;
    let cancelled = false;
    void getPresentationSkillState(palId)
      .then((s: { attached: boolean }) => {
        if (!cancelled) setSkillAttached(s.attached);
      })
      .catch(() => {
        if (!cancelled) setSkillAttached(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isTavus, palId]);

  // Converge toward intent as soon as the document finishes indexing, so the
  // trainer doesn't have to save again just to activate the skill.
  const reconciledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isTavus || !palId) return;
    if (!presentationEnabled || presentationBlocked) return;
    const documentId = selectedDoc?.document_id;
    if (!documentId || skillAttached !== false) return;
    if (reconciledRef.current === documentId) return;
    reconciledRef.current = documentId;
    void attachPresentationSkill({ palId, documentId })
      .then(() => setSkillAttached(true))
      .catch(() => {
        reconciledRef.current = null;
      });
  }, [
    isTavus,
    palId,
    presentationEnabled,
    presentationBlocked,
    selectedDoc?.document_id,
    skillAttached,
  ]);

  /** Register one uploaded source with the Tavus knowledge base, now. */
  async function registerTavusDoc(key: string, fileName: string, objectId: string) {
    if (!initial?.id) {
      toast.error("Save the scenario once before adding documents to Tavus.");
      return;
    }
    setRegisteringKey(key);
    try {
      const entry = await registerSourceDocument({
        item: { kind: "upload", object_id: objectId, file_name: fileName } as ScenarioSourceItem,
        scenarioId: initial.id,
      });
      setDocEntries((prev) => [entry, ...prev.filter((e) => e.source_key !== key)]);
      setKnowledgeDocSourceKey(key);
      clearError("knowledge");
      toast.success(`"${fileName}" added to Tavus — processing.`);
    } catch (err) {
      toast.error((err as Error)?.message || "Could not add this document to Tavus.");
    } finally {
      setRegisteringKey(null);
    }
  }

  /** Delete the Tavus document and drop the pairing. */
  async function unregisterTavusDoc(entry: TavusKnowledgeDocument) {
    setRegisteringKey(entry.source_key);
    try {
      if (entry.status !== "missing") await deleteTavusDocuments([entry.document_id]);
      setDocEntries((prev) => prev.filter((e) => e.source_key !== entry.source_key));
      if (knowledgeDocSourceKey === entry.source_key) {
        setKnowledgeDocSourceKey("");
        setPresentationEnabled(false);
      }
    } finally {
      setRegisteringKey(null);
    }
  }

  // Tavus retrieval replaces the generated knowledge entirely: with a document
  // attached, the prompt carries only persona + instructions so the avatar has
  // a single source of truth.
  const tavusRagActive = isTavus && mode === "conversational" && !!knowledgeDocSourceKey;

  // Verify stored D-ID pairings once on load — a document deleted upstream
  // comes back as `missing` so its source can be registered again.
  const didVerifiedRef = useRef(false);
  useEffect(() => {
    if (!isDid || didVerifiedRef.current) return;
    const stored = seed.didKnowledgeDocuments ?? [];
    const baseId = seed.didKnowledgeId ?? "";
    if (!stored.length || !baseId) return;
    didVerifiedRef.current = true;
    let cancelled = false;
    void verifyDidDocuments(baseId, stored).then((fresh) => {
      if (!cancelled) setDidDocEntries(fresh);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDid, initial?.id]);

  // Poll while D-ID indexes — saving is blocked until every entry is terminal.
  const didPollableIds = didDocEntries
    .filter((e) => !isTerminalDidDocumentStatus(e.status) && e.status !== "missing")
    .map((e) => e.document_id)
    .join(",");
  useEffect(() => {
    if (!didPollableIds || !didKnowledgeId) return;
    let cancelled = false;
    const ids = didPollableIds.split(",");
    const timer = window.setInterval(async () => {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            return {
              id,
              ...(await refreshDidDocumentStatus({
                knowledgeId: didKnowledgeId,
                documentId: id,
              })),
            };
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      setDidDocEntries((prev) =>
        prev.map((e) => {
          const hit = results.find((r) => r?.id === e.document_id);
          return hit ? { ...e, status: hit.status, error_message: hit.error_message } : e;
        }),
      );
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [didPollableIds, didKnowledgeId]);

  /** Register one uploaded source in this scenario's D-ID knowledge base. */
  async function registerDidDoc(key: string, fileName: string, objectId: string) {
    if (!initial?.id) {
      toast.error("Save the scenario once before adding documents to D-ID.");
      return;
    }
    setDidRegisteringKey(key);
    try {
      const { knowledgeId, entry } = await registerDidSourceDocument({
        item: { kind: "upload", object_id: objectId, file_name: fileName } as ScenarioSourceItem,
        scenarioId: initial.id,
        scenarioName: name,
        knowledgeId: didKnowledgeId || undefined,
      });
      setDidKnowledgeId(knowledgeId);
      setDidDocEntries((prev) => [entry, ...prev.filter((e) => e.source_key !== key)]);
      clearError("knowledge");
      toast.success(`"${fileName}" added to D-ID — indexing.`);
    } catch (err) {
      toast.error((err as Error)?.message || "Could not add this document to D-ID.");
    } finally {
      setDidRegisteringKey(null);
    }
  }

  /** Delete the D-ID document and drop the pairing. */
  async function unregisterDidDoc(entry: DidKnowledgeDocument) {
    setDidRegisteringKey(entry.source_key);
    try {
      if (entry.status !== "missing" && didKnowledgeId) {
        await deleteDidDocuments(didKnowledgeId, [entry.document_id]);
      }
      setDidDocEntries((prev) => prev.filter((e) => e.source_key !== entry.source_key));
    } finally {
      setDidRegisteringKey(null);
    }
  }

  // Same rule as Tavus: with a knowledge base attached in conversational mode,
  // retrieval is the single source of truth and no knowledge text goes into the prompt.
  // The presentation toggle mirrors the save-path gate: a live selection OR
  // any persisted display source. A saved upload whose draft no longer
  // matches still counts, so the switch never hides for a configured document.
  const didHasDisplaySource = !!display?.selection || !!initial?.content?.display?.source;
  const didRagActive =
    isDid &&
    mode === "conversational" &&
    didDocEntries.some((e) => e.status === "ready" || e.status === "processing");

  // Kaltura: uploads are sent to Kaltura natively; public URL sources are
  // registered as web sources. The record is rewritten from the current set.
  const kalturaDocOptions = useMemo(
    () =>
      sources.flatMap((s) => {
        if (s.kind === "upload" && s.object_id) {
          return [
            {
              key: `upload:${s.object_id}`,
              label: s.file_name,
              item: {
                kind: "upload",
                object_id: s.object_id,
                file_name: s.file_name,
              } as ScenarioSourceItem,
            },
          ];
        }
        if (s.kind === "url" && s.url.trim()) {
          const url = s.url.trim();
          return [
            { key: `url:${url}`, label: url, item: { kind: "url", url } as ScenarioSourceItem },
          ];
        }
        return [];
      }),
    [sources],
  );
  const kalturaSourcesDirty = useMemo(() => {
    const live = new Set(kalturaDocOptions.map((o) => o.key));
    if (live.size !== kalturaSyncedKeys.length) return true;
    return kalturaSyncedKeys.some((k) => !live.has(k));
  }, [kalturaDocOptions, kalturaSyncedKeys]);

  /** Rewrite the scenario's Kaltura knowledge record from current sources. */
  async function syncKalturaKnowledge() {
    if (!initial?.id) {
      toast.error("Save the scenario once before syncing knowledge to Kaltura.");
      return;
    }
    setKalturaSyncing(true);
    try {
      const res = await syncKalturaKnowledgeSources({
        items: kalturaDocOptions.map((o) => o.item),
        scenarioId: initial.id,
        scenarioName: name,
        knowledgeId: kalturaKnowledgeId ?? undefined,
        existingSources: kalturaKnowledgeSources,
      });
      setKalturaKnowledgeId(res.knowledgeId);
      setKalturaSyncedKeys(res.sourceKeys);
      setKalturaKnowledgeSources(res.sources);
      setKalturaSyncedAt(res.syncedAt);
      clearError("knowledge");
      toast.success(`Synced ${res.sourceKeys.length} source(s) to Kaltura.`);
    } catch (err) {
      toast.error((err as Error)?.message || "Could not sync knowledge to Kaltura.");
    } finally {
      setKalturaSyncing(false);
    }
  }

  const kalturaRagActive =
    isKaltura && mode === "conversational" && !!kalturaKnowledgeId && kalturaSyncedKeys.length > 0;
  const ragActive = tavusRagActive || didRagActive || kalturaRagActive;

  // Indexing must finish before saving: the agent write attaches the base and a
  // half-indexed base answers nothing. Only relevant in conversational mode,
  // because other modes never attach the knowledge base to the agent.
  const didDocsIndexing =
    isDid && mode === "conversational" && didDocEntries.some((e) => e.status === "processing");

  // Drop a selection whose source was removed, or that no longer applies.

  useEffect(() => {
    if (!knowledgeDocSourceKey) return;
    const stillValid =
      isTavus &&
      mode === "conversational" &&
      tavusDocOptions.some((o) => o.key === knowledgeDocSourceKey);
    if (!stillValid) {
      setKnowledgeDocSourceKey("");
      setPresentationEnabled(false);
    }
  }, [knowledgeDocSourceKey, tavusDocOptions, isTavus, mode]);

  const promptInputs = useMemo(
    () => ({
      mode,
      persona: modeMeta.showPersona ? persona : "",
      knowledge: ragActive
        ? ""
        : mode === "multiple_choice"
          ? serializeQuestions(mcqQuestions)
          : knowledge,
      instructions: modeMeta.showInstructions ? instructions : "",
      roleplay: mode === "role_play" ? roleplay : undefined,
    }),
    [mode, modeMeta, persona, knowledge, instructions, mcqQuestions, roleplay, ragActive],
  );
  const generatedPrompt = useMemo(() => buildContextPrompt(promptInputs), [promptInputs]);

  const [pendingMode, setPendingMode] = useState<InteractionMode | null>(null);
  const [pendingProvider, setPendingProvider] = useState<ProviderId | null>(null);

  // D-ID has no runtime language parameter — the LLM only speaks the
  // language we bake into its instructions, so keep one managed language
  // line at the top of the Instructions field. The opening sentence is
  // NOT injected: it ships natively as the agent's `greetings`, which is
  // verbatim. Legacy injected lines are stripped on edit.
  useEffect(() => {
    if (provider !== "did") return;
    const DID_LANG_PREFIX = "Always respond in ";
    const DID_OPEN_PREFIX = "Open the conversation with: ";
    let langName = "";
    if (language) {
      try {
        const dn = new Intl.DisplayNames(["en"], { type: "language" });
        langName = dn.of(language.split("-")[0].toLowerCase()) || language;
      } catch {
        langName = language;
      }
    }
    const langLine = langName ? `${DID_LANG_PREFIX}${langName}.` : "";
    setInstructions((prev) => {
      const rest = prev
        .split("\n")
        .filter((l) => !l.startsWith(DID_LANG_PREFIX) && !l.startsWith(DID_OPEN_PREFIX))
        .join("\n")
        .replace(/^\n+/, "");
      const managed = langLine;
      const next = managed ? (rest ? `${managed}\n\n${rest}` : managed) : rest;
      return next === prev ? prev : next;
    });
  }, [provider, language]);

  // If the currently selected provider doesn't support the current interaction
  // mode (e.g. DID after switching to Open questions), snap to the first
  // supported provider and clear provider-specific selections so the picker
  // doesn't hold stale IDs from the previous provider.
  useEffect(() => {
    if (availableProviders.length === 0) return;
    if (availableProviders.includes(provider)) return;
    applyProviderChange(availableProviders[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProviders, provider]);

  function hasModeInfluencedContent() {
    return (
      knowledge.trim().length > 0 ||
      persona.trim().length > 0 ||
      instructions.trim().length > 0 ||
      sources.length > 0
    );
  }
  function applyModeChange(next: InteractionMode) {
    setMode(next);
    setKnowledge("");
    setPersona("");
    setInstructions("");
    setSources([]);
    setMcqQuestions([]);
    setMcqOpts({ count: DEFAULT_GENERATE_MCQ_COUNT, passRate: DEFAULT_PASS_RATE });
    setRoleplay(defaultRolePlayConfig());
    // Book-a-meeting CTA is conversational-only — clear when leaving.
    if (next !== "conversational") {
      setCtaType("none");
      setCtaUrl("");
      clearError("ctaUrl");
    }
    setErrors((prev) => {
      const { knowledge: _k, persona: _p, instructions: _i, ...rest } = prev;
      return rest;
    });
  }

  function requestModeChange(next: InteractionMode) {
    if (next === mode) return;
    if (!hasModeInfluencedContent()) {
      setMode(next);
      if (next !== "conversational") {
        setCtaType("none");
        setCtaUrl("");
        clearError("ctaUrl");
      }
      return;
    }
    setPendingMode(next);
  }

  function applyProviderChange(next: ProviderId) {
    setProvider(next);
    // Face/voice catalogs are provider-specific — clear both slots so
    // the picker doesn't briefly show a stale id from the other side.
    setAvatarId("");
    setVoiceId("");
    clearError("avatarId");
    clearError("voiceId");
    clearError("language");
    clearError("faceId");
    // Provider-specific options don't carry across catalogs either. The save
    // pipeline already strips the foreign block, so reset them here too and
    // the editor matches what will actually be persisted.
    setBackgroundId(DID_BACKGROUND_NONE);
    setBackgroundUrl("");
    setDidKnowledgeId("");
    setDidDocEntries([]);
    setDidGrounded(false);
    didVerifiedRef.current = false;
    setDocEntries([]);
    setKnowledgeDocSourceKey("");
    verifiedRef.current = false;
    setPresentationEnabled(false);
    setInternetSearchEnabled(false);
    setDisableCameraSharing(false);
    setDisableScreenSharing(false);
    setKalturaDisableScreenShare(false);
    setKalturaKnowledgeId(null);
    setKalturaSyncedKeys([]);
    setKalturaKnowledgeSources([]);
    setKalturaSyncedAt("");
  }
  function requestProviderChange(next: ProviderId) {
    if (next === provider) return;
    if (hasModeInfluencedContent() || avatarId || voiceId) {
      setPendingProvider(next);
      return;
    }
    applyProviderChange(next);
  }
  function clearError(key: keyof Errors) {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  }
  function validate(): Errors {
    const e: Errors = {};
    if (!name.trim()) e.name = "Name is required";
    if (publishMode !== "hidden" && !description.trim()) {
      e.description = "Description is required when the scenario is not hidden";
    }
    if (mode === "multiple_choice") {
      const rate = mcqOpts.passRate;
      if (typeof rate !== "number" || !Number.isFinite(rate) || rate < 1 || rate > 100) {
        e.mcqPassingRate = "Enter a passing rate between 1 and 100";
      }
      if (mcqQuestions.length === 0) {
        e.mcq = "Add or generate at least one question";
      } else {
        const bad = mcqQuestions.find((q) => {
          if (!q.question.trim()) return true;
          const filled = q.answers.filter((a) => a.text.trim().length > 0);
          if (filled.length < 1) return true;
          const min = q.amount_of_correct_answers_to_pass;
          if (!Number.isFinite(min) || min < 1 || min > filled.length) return true;
          return false;
        });
        if (bad)
          e.mcq =
            "Each question needs text, at least one valid answer, and a minimum-to-pass within the answer count";
      }
    } else if (mode === "role_play") {
      const rpErrors = validateRolePlay(roleplay);
      if (rpErrors.length > 0) {
        e.knowledge = rpErrors.map((er) => er.message).join(" · ");
      }
    } else if (!ragActive && !knowledge.trim()) {
      e.knowledge = `${modeMeta.textareaLabel} is required — generate or paste content`;
    }
    if (isTavus) {
      if (!avatarId) e.faceId = "Pick a Tavus face";
    } else if (isKaltura) {
      // Face + voice are picked separately; language is an optional filter.
      if (!avatarId) e.avatarId = "Pick a face";
      if (!voiceId) e.voiceId = "Pick a voice";
    } else {
      if (!avatarId) e.avatarId = "Pick an avatar";
      if (!voiceId) e.voiceId = "Pick a voice";
      else if (provider === "liveavatar" && forceByoLivekit && !isByoCompatibleVoice(pickers.voices.options, voiceId))
        e.voiceId = "Powtoon LiveKit needs an imported voice — pick one from Custom / Imported";
      if (!language) e.language = "Pick a language";
    }
    const trimmedCta = ctaUrl.trim();
    const trimmedRedirect = redirectUrl.trim();
    if (ctaType === "sales") {
      if (!trimmedCta) e.ctaUrl = "Booking URL is required for the Sales CTA";
      else if (!/^https?:\/\//i.test(trimmedCta))
        e.ctaUrl = "Booking URL must start with http:// or https://";
    }
    if (trimmedRedirect && !/^https?:\/\//i.test(trimmedRedirect)) {
      e.redirectUrl = "Redirect URL must start with http:// or https://";
    }
    // A display URL learners can't see is not shippable.
    if (display?.selection?.kind === "url") {
      const probe = getCachedUrlEmbedResult(display.selection.url);
      if (probe && !isDisplayableVerdict(probe.verdict)) {
        e.display =
          probe.verdict === "blocked"
            ? "The selected display URL refuses to be embedded — learners would see nothing. Pick a file or another URL."
            : "The selected display URL can't be reached — learners would see nothing. Pick a file or another URL.";
      }
    }
    return e;
  }

  async function handle(ev: FormEvent) {
    ev.preventDefault();
    // The agent write attaches the knowledge base, so every document must be
    // done indexing first — a half-indexed base answers nothing.
    if (didDocsIndexing) {
      toast.error("D-ID is still indexing a knowledge document — try again in a moment.");
      return;
    }
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      toast.error("Please fix the highlighted fields before saving");
      const firstKey = Object.keys(e)[0];
      const node = fieldRefs.current[firstKey];
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof (node as HTMLInputElement).focus === "function") {
          (node as HTMLInputElement).focus({ preventScroll: true });
        }
      }
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const currentQuestionnaire: MCQConfig | null =
        mode === "multiple_choice"
          ? {
              id: initialQuestionnaireIdRef.current,
              pass_rate: mcqOpts.passRate,
              questions: mcqQuestions,
            }
          : null;

      // Build a provider-agnostic form snapshot and let the provider's
      // adapter serialize it into persisted content. Symmetric to
      // `adapter.toFormState` on read — same UI, different backends.
      const adapter = getAdapterByProvider(provider);
      const nextContent = adapter.toContent({
        name,
        description,
        thumb_url: initial?.thumb_url,
        avatarId,
        voiceId,
        language,
        mode,
        persona,
        knowledge: promptInputs.knowledge,
        instructions: modeMeta.showInstructions
          ? instructions.trim() || (mode === "multiple_choice" ? DEFAULT_MCQ_INSTRUCTIONS : "")
          : "",
        openingText,
        generatedPrompt: buildContextPrompt(promptInputs),
        roleplay,
        publishMode,
        autoStart,
        ctaType,
        ctaUrl,
        redirectUrl,
        display: resolveDisplayForPersist(display, sources),
        initialInputs: initialInputsRef.current,
        previous: initial?.content,
        
        disableCameraSharing,
        disableScreenSharing,
        knowledgeDocumentSourceKey: knowledgeDocSourceKey || undefined,
        presentationEnabled,
        internetSearchEnabled,
        // Pairings registered in the editor. Without these the save pipeline
        // sees no documents and silently drops the attachment + skill.
        knowledgeDocuments: docEntries,
        backgroundId,
        backgroundUrl,
        // D-ID knowledge base registered in the editor; save only reconciles it.
        didKnowledgeId: didKnowledgeId || undefined,
        didKnowledgeDocuments: didDocEntries,
        didKnowledgeGrounded: didGrounded,
        didPresentDocument,
        didPresentationToolId: initial?.content?.did?.presentation_tool_id,
        didCloseToolId: initial?.content?.did?.close_tool_id,
        didPresentationToolIds: initial?.content?.did?.presentation_tool_ids,
        // Kaltura knowledge record synced in the editor; save attaches/detaches it.
        kalturaKnowledgeId: kalturaKnowledgeId ?? undefined,
        kalturaKnowledgeSourceKeys: kalturaSyncedKeys,
        kalturaKnowledgeSources,
        kalturaKnowledgeSyncedAt: kalturaSyncedAt || undefined,
        kalturaWebSearch,
        kalturaDisableScreenShare,
        kalturaPresentDocument,
      });
      // LiveAvatar session-runtime mode is org-scoped; keep this rule at
      // the form level for now (not part of the adapter contract).
      if (provider === "liveavatar") {
        nextContent.force_byo_livekit = forceByoLivekit;
        nextContent.mode = forceByoLivekit ? "lite" : liveAvatarModeForOrg(orgDomain);
      }
      nextContent.show_ai_notice = showAiNotice;

      const selectedAvatar = pickers.avatars.options.find((o) => o.id === avatarId);
      // Adapters know which persisted field holds the "avatar" for their
      // provider; ask them for the initial value so we can detect a change.
      const initialSeed = initial ? getAdapter(initial).toFormState(initial) : {};
      const initialActiveAvatarId = initialSeed.avatarId ?? "";
      const avatarChanged =
        avatarId !== initialActiveAvatarId || (!initial && !!selectedAvatar?.preview_url);
      // The stored thumbnail is the avatar flattened over the scene
      // background, so a background change alone must refresh it too.
      const backgroundPreviewUrl = didBackgroundPreview(backgroundId, backgroundUrl || undefined);
      const backgroundChanged =
        (backgroundId || "none") !== (initialSeed.backgroundId ?? "none") ||
        (backgroundUrl || "") !== (initialSeed.backgroundUrl ?? "");

      // One provider-agnostic meta object; provider-specific save meta
      // (Tavus pal id, previous ElevenLabs voice, …) comes from the
      // adapter and is spread in — invisible to this form.
      const providerMeta = adapter.collectSaveMeta(initial) as Partial<SaveScenarioMeta>;
      await onSubmit(
        {
          name,
          description,
          thumb_url: initial?.thumb_url,
          content: nextContent,
        },
        {
          avatarPreviewUrl: selectedAvatar?.preview_url,
          avatarChanged,
          backgroundPreviewUrl,
          backgroundChanged,
          sourceDrafts: sources,
          initialInputs: initialInputsRef.current,
          questionnaire: currentQuestionnaire,
          initialQuestionnaire: initialQuestionnaireRef.current,
          initialQuestionnaireIds: questionnaireIdsOf(initial?.content),
          roleplay: mode === "role_play" ? roleplay : null,
          initialRoleplay: initialRoleplayRef.current,
          initialRoleplayIds: roleplayIdsOf(initial?.content),
          display,
          provider,
          // Reconciled against the Tavus knowledge base during save.
          knowledgeDocumentSourceKey: knowledgeDocSourceKey || undefined,
          ...providerMeta,
        },
      );
    } finally {
      setBusy(false);
    }
  }

  const errorRing = "border-destructive focus-visible:ring-destructive";

  return (
    <form
      id="scenario-form"
      onSubmit={handle}
      noValidate
      aria-busy={busy}
      className="space-y-5 max-w-3xl"
    >
      {/* Everything editable lives inside this fieldset: while a save is in
          flight it is disabled (covers native inputs and Radix triggers) and
          made inert (covers custom click surfaces like tiles and dropzones). */}
      <fieldset
        disabled={busy}
        className={cn(
          "m-0 min-w-0 space-y-5 border-0 p-0",
          busy && "pointer-events-none opacity-60",
        )}
      >
        <div className="space-y-1.5">
          <Label htmlFor="name">
            Name
            <Req />
          </Label>
          <Input
            id="name"
            ref={(el) => {
              fieldRefs.current.name = el;
            }}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearError("name");
            }}
            aria-invalid={!!errors.name}
            className={cn(errors.name && errorRing)}
            required
          />
          <FieldError message={errors.name} />
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="block text-lg font-semibold text-foreground">
              Interaction mode
              <Req />
            </Label>
            <p className="text-sm text-muted-foreground">
              Sets how the avatar interacts, and which fields you'll fill in next.
            </p>
          </div>
          <InteractionModePicker value={mode} onChange={requestModeChange} />
        </div>

        {availableProviders.length > 1 && (
          <ProviderPicker
            value={provider}
            available={availableProviders}
            onChange={requestProviderChange}
            disabled={isEditing}
            helperText="Provider can't be changed after creation."
          />
        )}

        {mode === "role_play" ? (
          <div
            ref={(el) => {
              fieldRefs.current.knowledge = el;
            }}
          >
            <RolePlayEditor
              value={roleplay}
              onChange={(next) => {
                setRoleplay(next);
                clearError("knowledge");
              }}
              sources={sources}
              onSourcesChange={setSources}
              scenarioId={initial?.id}
              showErrors={!!errors.knowledge}
            />
            <FieldError message={errors.knowledge} />
          </div>
        ) : (
          <>
            <SourceInput
              mode={mode}
              value={sources}
              onChange={setSources}
              scenarioId={initial?.id}
              onGenerated={(content) => {
                setKnowledge(content);
                clearError("knowledge");
              }}
              generating={generatingKnowledge}
              onGeneratingChange={setGeneratingKnowledge}
              mcqOptions={mode === "multiple_choice" ? mcqOpts : undefined}
              onMcqOptionsChange={setMcqOpts}
              onMcqQuestions={(qs) => {
                setMcqQuestions(normalizeIncoming(qs));
                if (!instructions.trim()) setInstructions(DEFAULT_MCQ_INSTRUCTIONS);
                clearError("mcq");
              }}
            />

            {mode === "multiple_choice" ? (
              <div
                className="space-y-1.5"
                ref={(el) => {
                  fieldRefs.current.mcq = el;
                }}
              >
                <Label>
                  {modeMeta.textareaLabel}
                  <Req />
                </Label>
                <TextareaWrap
                  generating={generatingKnowledge}
                  generatingLabel="Generating questions…"
                >
                  <QuestionListEditor
                    questions={mcqQuestions}
                    onChange={(qs) => {
                      setMcqQuestions(qs);
                      clearError("mcq");
                    }}
                    disabled={generatingKnowledge}
                    hasError={!!errors.mcq}
                  />
                </TextareaWrap>
                <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Passing rate (%)
                      <Req />
                    </Label>
                    <Select
                      value={String(Math.round(mcqOpts.passRate))}
                      onValueChange={(val) => {
                        setMcqOpts({ ...mcqOpts, passRate: Number(val) });
                        clearError("mcqPassingRate");
                      }}
                    >
                      <SelectTrigger
                        aria-invalid={!!errors.mcqPassingRate}
                        className={cn("h-9 w-[110px]", errors.mcqPassingRate && errorRing)}
                      >
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        {[50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}%
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError message={errors.mcqPassingRate} />
                  </div>
                  <p className="text-xs text-muted-foreground pb-2">
                    Minimum score a learner needs to pass this quiz.
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {mcqQuestions.length} question{mcqQuestions.length === 1 ? "" : "s"}
                  </span>
                  <FieldError message={errors.mcq} />
                </div>
              </div>
            ) : ragActive ? (
              <div className="rounded-lg border border-dashed bg-muted/40 p-3">
                <p className="text-sm font-medium">Knowledge comes from the attached document</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {singleProvider ? "The avatar" : isDid ? "D-ID" : isKaltura ? "Kaltura" : "Tavus"} retrieves answers from the
                  document you {isKaltura ? "synced" : "attached"} under {singleProvider ? "Additional features" : "Provider features"}, so no
                  knowledge text is sent in the prompt.{" "}
                  {isKaltura
                    ? "Remove the synced sources to write knowledge here instead."
                    : "Detach the document to write knowledge here instead."}
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="kb">
                  {modeMeta.textareaLabel}
                  <Req />
                </Label>
                <TextareaWrap
                  generating={generatingKnowledge}
                  generatingLabel={`Generating ${modeMeta.textareaLabel.toLowerCase()}…`}
                >
                  <Textarea
                    id="kb"
                    ref={(el) => {
                      fieldRefs.current.knowledge = el;
                    }}
                    value={knowledge}
                    onChange={(e) => {
                      setKnowledge(e.target.value);
                      clearError("knowledge");
                    }}
                    placeholder={modeMeta.textareaPlaceholder}
                    aria-invalid={!!errors.knowledge}
                    disabled={generatingKnowledge}
                    className={cn("min-h-[220px] font-mono text-xs", errors.knowledge && errorRing)}
                  />
                </TextareaWrap>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {knowledge.length.toLocaleString()} characters
                  </span>
                  <FieldError message={errors.knowledge} />
                </div>
              </div>
            )}
          </>
        )}

        {modeMeta.showPersona && (
          <div className="space-y-1.5">
            <Label htmlFor="persona">Persona</Label>
            <p className="text-xs text-muted-foreground">
              Who the avatar is — name, role, tone, and speaking style. Used every turn to shape how
              the avatar speaks.
            </p>
            <TextareaWrap
              generating={generatingPersona}
              generatingLabel={persona.trim() ? "Enhancing…" : "Generating persona…"}
              action={
                <FieldGenerateButton
                  value={persona}
                  generating={generatingPersona}
                  onClick={() => void handleGenerateField("persona")}
                />
              }
            >
              <Textarea
                id="persona"
                ref={(el) => {
                  fieldRefs.current.persona = el;
                }}
                value={persona}
                onChange={(e) => {
                  setPersona(e.target.value);
                  clearError("persona");
                }}
                placeholder="e.g. Dr. Sara, a friendly oncology nurse. Warm, plain language, patient explanations."
                aria-invalid={!!errors.persona}
                disabled={generatingPersona}
                className={cn("min-h-[120px] pb-12", errors.persona && errorRing)}
              />
            </TextareaWrap>
            <FieldError message={errors.persona} />
          </div>
        )}

        {modeMeta.showInstructions && (
          <div className="space-y-1.5">
            <Label htmlFor="instructions">Instructions</Label>
            <p className="text-xs text-muted-foreground">
              {mode === "multiple_choice"
                ? "Rules for how to run the questionnaire. The default rules already cover scoring; edit only to add domain-specific behavior."
                : mode === "role_play"
                  ? "Optional extra rules for the avatar during the role play (tone, forbidden topics, policies). These support the scenario — they cannot change the roles or how the session ends."
                  : "Rules the avatar must always (or never) follow. Applied on top of the Persona and Knowledge."}
            </p>
            <Textarea
              id="instructions"
              ref={(el) => {
                fieldRefs.current.instructions = el;
              }}
              value={instructions}
              onChange={(e) => {
                setInstructions(e.target.value);
                clearError("instructions");
              }}
              placeholder="e.g. Always cite the source document. Never recommend specific medications."
              aria-invalid={!!errors.instructions}
              className={cn("min-h-[120px]", errors.instructions && errorRing)}
            />
            <FieldError message={errors.instructions} />
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="opening">Opening text</Label>
          <TextareaWrap
            generating={generatingOpening}
            generatingLabel={openingText.trim() ? "Enhancing…" : "Generating opening…"}
            action={
              <FieldGenerateButton
                value={openingText}
                generating={generatingOpening}
                onClick={() => void handleGenerateField("opening_text")}
              />
            }
          >
            <Textarea
              id="opening"
              value={openingText}
              onChange={(e) => setOpeningText(e.target.value)}
              placeholder="Hi there, ready to get started?"
              disabled={generatingOpening}
              className="min-h-[80px] pb-12"
            />
          </TextareaWrap>
          <p className="text-xs text-muted-foreground">
            First line the avatar says to the learner.
          </p>
        </div>

        <Collapsible open={promptOpen} onOpenChange={setPromptOpen} className="space-y-1.5">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 text-left rounded-md py-1 hover:bg-accent/40"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  promptOpen && "rotate-90",
                )}
              />
              <Label htmlFor="prompt" className="cursor-pointer">
                System prompt
              </Label>
              <span className="text-xs text-muted-foreground">
                Auto-generated from the fields above. Read-only.
              </span>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Textarea
              id="prompt"
              value={generatedPrompt}
              readOnly
              className="min-h-[220px] font-mono text-xs bg-muted/40"
            />
          </CollapsibleContent>
        </Collapsible>

        <section className="space-y-4 rounded-lg border bg-card p-4">
          <div>
            <h3 className="text-base font-semibold">Avatar &amp; voice</h3>
            <p className="text-sm text-muted-foreground">
              {isKaltura
                ? "Pick the face and voice for this scenario — the pair is provisioned for you on save."
                : "Pick the avatar, voice, and language for this scenario."}
            </p>
          </div>
          <div
            ref={(el) => {
              fieldRefs.current.avatarId = el;
              fieldRefs.current.faceId = el;
            }}
            className={cn(
              (errors.avatarId || errors.faceId) && "rounded-lg ring-1 ring-destructive p-2 -m-2",
            )}
          >
            <AvatarPicker
              value={avatarId}
              options={pickers.avatars.options}
              isLoading={pickers.avatars.isLoading}
              error={pickers.avatars.error}
              onChange={(v) => {
                setAvatarId(v);
                clearError("avatarId");
                clearError("faceId");
                const picked = pickers.avatars.options.find((o) => o.id === v);
                const derived = pickers.deriveDefaults(picked, {
                  voices: pickers.voices.options,
                  languages: pickers.languages.options,
                });
                if (derived.voiceId !== undefined && (provider !== "liveavatar" || !voiceId)) {
                  setDisplayVoiceId(derived.voiceId);
                  clearError("voiceId");
                }
                if (derived.language !== undefined) {
                  setLanguage(derived.language);
                  clearError("language");
                }
              }}
            />
            <FieldError message={errors.avatarId || errors.faceId} />
          </div>
          {pickers.showVoicePicker && (
            <div className="grid gap-4 sm:grid-cols-4">
              <div
                ref={(el) => {
                  fieldRefs.current.voiceId = el;
                }}
                className="sm:col-span-3"
              >
                {provider === "liveavatar" ? (
                  <VoiceBrowserDialog
                    label={pickers.languageRequired ? "Voice *" : "Voice"}
                    value={displayVoiceId}
                    onChange={(v) => {
                      setDisplayVoiceId(v);
                      clearError("voiceId");
                    }}
                    options={filteredVoiceOptions}
                    loading={pickers.voices.isLoading}
                    error={pickers.voices.error}
                    previewFetcher={pickers.voices.previewFetcher}
                    languageName={(c) => (c ? languageName(c) : undefined)}
                  />
                ) : (
                <PickerSelect
                  label={pickers.languageRequired ? "Voice *" : "Voice"}
                  value={displayVoiceId}
                  onChange={(v) => {
                    setDisplayVoiceId(v);
                    clearError("voiceId");
                  }}
                  options={pickers.voices.options}
                  loading={pickers.voices.isLoading}
                  error={pickers.voices.error}
                  placeholder="Choose a voice"
                  showPreview
                  previewFetcher={pickers.voices.previewFetcher}
                />
                )}
                <FieldError message={errors.voiceId} />
              </div>
              <div
                ref={(el) => {
                  fieldRefs.current.language = el;
                }}
                className="sm:col-span-1"
              >
                <PickerSelect
                  label={pickers.languageRequired ? "Language *" : "Language"}
                  value={language}
                  onChange={(v) => {
                    setLanguage(v);
                    clearError("language");
                  }}
                  options={pickers.languages.options}
                  loading={pickers.languages.isLoading}
                  error={pickers.languages.error}
                  placeholder={pickers.languageRequired ? "Choose a language" : "Auto"}
                />
                <FieldError message={errors.language} />
              </div>
            </div>
          )}
        </section>

        {isDid && (
          <section className="space-y-4 rounded-lg border bg-card p-4">
            <div>
              <h3 className="text-base font-semibold">{singleProvider ? "Additional features" : "Provider features"}</h3>
              <p className="text-sm text-muted-foreground">
                {singleProvider ? "Extra options for this avatar." : "Options specific to D-ID. Other providers offer a different set."}
              </p>
            </div>
            <BackgroundPicker
              value={backgroundId}
              onChange={setBackgroundId}
              helper="Applies to green-screen avatars — D-ID composites the scene when the session renders."
              options={DID_BACKGROUND_TILES}
              uploadComingSoon
              customId={DID_BACKGROUND_CUSTOM}
              customUrl={backgroundUrl || undefined}
              onUpload={async (file) => {
                const dataUrl = await readFileAsDataUrl(file);
                const { url } = await did.uploadBackground({
                  data_url: dataUrl,
                  filename: file.name,
                });
                setBackgroundUrl(url);
                setBackgroundId(DID_BACKGROUND_CUSTOM);
              }}
              onRemoveCustom={() => {
                setBackgroundUrl("");
                if (backgroundId === DID_BACKGROUND_CUSTOM) setBackgroundId(DID_BACKGROUND_NONE);
              }}
            />

            {mode === "conversational" && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label>Knowledge documents</Label>
                  <p className="text-xs text-muted-foreground">
                    Add uploaded files to this scenario's D-ID knowledge base. D-ID indexes them and
                    retrieves from them live, replacing the knowledge text in the prompt — persona
                    and instructions still apply. The knowledge base belongs to this scenario only.
                  </p>
                  {tavusDocOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {pendingTavusDocs
                        ? "Save the scenario to finish uploading your files, then add them here."
                        : "Upload a file in the knowledge sources above to use it as a document."}
                    </p>
                  ) : (
                    <ul className="divide-y rounded-md border">
                      {tavusDocOptions.map((o) => {
                        const entry = didDocEntries.find((e) => e.source_key === o.key);
                        const busyRow = didRegisteringKey === o.key;
                        return (
                          <li key={o.key} className="flex items-center gap-3 p-2.5">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm">{o.label}</p>
                              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                {!entry
                                  ? "Not in D-ID yet"
                                  : entry.status === "ready"
                                    ? "Ready in D-ID"
                                    : entry.status === "error"
                                      ? "D-ID could not process this file"
                                      : entry.status === "missing"
                                        ? "No longer in D-ID — add it again"
                                        : "Indexing in D-ID…"}
                                {entry && entry.status === "processing" && (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                )}
                              </p>
                            </div>
                            {entry && entry.status !== "missing" ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={busyRow}
                                onClick={() => void unregisterDidDoc(entry)}
                              >
                                {busyRow ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  "Remove"
                                )}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={busyRow || !initial?.id}
                                title={!initial?.id ? "Save the scenario once first" : undefined}
                                onClick={() =>
                                  void registerDidDoc(o.key, o.label, o.objectId ?? "")
                                }
                              >
                                {busyRow ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  "Add to D-ID"
                                )}
                              </Button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {didHasDisplaySource && (
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="did-present-document">
                        Let the avatar present the document uploaded in the learner experience
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        The learner starts with the avatar alone. When it's useful, the avatar
                        brings the display document up on screen itself.
                      </p>
                    </div>
                    <Switch
                      id="did-present-document"
                      checked={didPresentDocument}
                      onCheckedChange={setDidPresentDocument}
                    />
                  </div>
                )}

                {didRagActive && (
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="did-grounded">Answer only from documents</Label>
                      <p className="text-xs text-muted-foreground">
                        On: the avatar sticks strictly to the attached documents. Off: it blends
                        them with the general knowledge.
                      </p>
                    </div>
                    <Switch
                      id="did-grounded"
                      checked={didGrounded}
                      onCheckedChange={setDidGrounded}
                    />
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {isKaltura && mode === "conversational" && (
          <section className="space-y-4 rounded-lg border bg-card p-4">
            <div>
              <h3 className="text-base font-semibold">{singleProvider ? "Additional features" : "Provider features"}</h3>
              <p className="text-sm text-muted-foreground">
                {singleProvider ? "Extra options for this avatar." : "Options specific to Kaltura. Other providers offer a different set."}
              </p>
            </div>
            <div className="space-y-3">
              <div
                aria-disabled
                className="pointer-events-none space-y-3 rounded-md border border-dashed p-3 opacity-60"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label>Knowledge documents</Label>
                    <Badge variant="secondary">Coming soon</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Upload scenario files to Kaltura and register public URLs in its knowledge base.
                    Kaltura's document ingestion is currently unavailable — we'll switch this back
                    on once their fix lands.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" disabled>
                  Sync to Kaltura
                </Button>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="kaltura-web-search">Live web search</Label>
                  <p className="text-xs text-muted-foreground">
                    Let the avatar look up current information on the web during the session and
                    show the sources it used.
                  </p>
                </div>
                <Switch
                  id="kaltura-web-search"
                  checked={kalturaWebSearch}
                  onCheckedChange={setKalturaWebSearch}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="kaltura-disable-screen-share">Disable screen sharing</Label>
                  <p className="text-xs text-muted-foreground">
                    Screen sharing is on by default: the learner gets a share-screen button and the
                    avatar sees snapshots of the shared screen. Turn this on to hide the button.
                  </p>
                </div>
                <Switch
                  id="kaltura-disable-screen-share"
                  checked={kalturaDisableScreenShare}
                  onCheckedChange={setKalturaDisableScreenShare}
                />
              </div>
              {didHasDisplaySource && (
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="kaltura-present-document">
                      Let the avatar present the document uploaded in the learner experience
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      The learner starts with the avatar alone. When it's useful, the avatar brings
                      the display document up on screen itself.
                    </p>
                  </div>
                  <Switch
                    id="kaltura-present-document"
                    checked={kalturaPresentDocument}
                    onCheckedChange={setKalturaPresentDocument}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {isTavus && (
          <section className="space-y-4 rounded-lg border bg-card p-4">
            <div>
              <h3 className="text-base font-semibold">{singleProvider ? "Additional features" : "Provider features"}</h3>
              <p className="text-sm text-muted-foreground">
                {singleProvider ? "Extra options for this avatar." : "Options specific to Tavus. Other providers offer a different set."}
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="disable-camera-sharing">Disable camera sharing</Label>
                  <p className="text-xs text-muted-foreground">
                    Hide the camera button so learners can't turn on their webcam.
                  </p>
                </div>
                <Switch
                  id="disable-camera-sharing"
                  checked={disableCameraSharing}
                  onCheckedChange={setDisableCameraSharing}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="disable-screen-sharing">Disable screen sharing</Label>
                  <p className="text-xs text-muted-foreground">
                    Hide the screen-share button so learners can't share a screen or tab.
                  </p>
                </div>
                <Switch
                  id="disable-screen-sharing"
                  checked={disableScreenSharing}
                  onCheckedChange={setDisableScreenSharing}
                />
              </div>
            </div>
            {mode === "conversational" && (
              <>
                <div className="space-y-2">
                  <Label>Use a document as knowledge</Label>
                  <p className="text-xs text-muted-foreground">
                    Add an uploaded file to Tavus, then set it as the knowledge source. Tavus
                    indexes it and retrieves from it live, replacing the knowledge text in the
                    prompt — persona and instructions still apply.
                  </p>
                  {tavusDocOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {pendingTavusDocs
                        ? "Save the scenario to finish uploading your files, then add them here."
                        : "Upload a file in the knowledge sources above to use it as a document."}
                    </p>
                  ) : (
                    <ul className="divide-y rounded-md border">
                      {tavusDocOptions.map((o) => {
                        const entry = docEntries.find((e) => e.source_key === o.key);
                        const busyRow = registeringKey === o.key;
                        const inUse = knowledgeDocSourceKey === o.key;
                        return (
                          <li key={o.key} className="flex items-center gap-3 p-2.5">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm">{o.label}</p>
                              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                {!entry
                                  ? "Not in Tavus yet"
                                  : entry.status === "ready"
                                    ? "Ready in Tavus"
                                    : entry.status === "error"
                                      ? "Tavus could not process this file"
                                      : entry.status === "missing"
                                        ? "No longer in Tavus — add it again"
                                        : "Processing in Tavus…"}
                                {entry &&
                                  !isTerminalDocumentStatus(entry.status) &&
                                  entry.status !== "missing" && (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  )}
                              </p>
                            </div>
                            {entry && entry.status !== "missing" ? (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={inUse ? "secondary" : "outline"}
                                  disabled={busyRow}
                                  onClick={() => {
                                    if (inUse) {
                                      setKnowledgeDocSourceKey("");
                                      setPresentationEnabled(false);
                                    } else {
                                      setKnowledgeDocSourceKey(o.key);
                                      clearError("knowledge");
                                    }
                                  }}
                                >
                                  {inUse ? "In use" : "Use as knowledge"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  disabled={busyRow}
                                  onClick={() => void unregisterTavusDoc(entry)}
                                >
                                  {busyRow ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    "Remove"
                                  )}
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={busyRow || !initial?.id}
                                title={!initial?.id ? "Save the scenario once first" : undefined}
                                onClick={() =>
                                  void registerTavusDoc(o.key, o.label, o.objectId ?? "")
                                }
                              >
                                {busyRow ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  "Add to Tavus"
                                )}
                              </Button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {!!knowledgeDocSourceKey && (
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="tavus-presentation">Presentation skill</Label>
                      <p className="text-xs text-muted-foreground">
                        {presentationBlocked
                          ? "Available once processing in Tavus finishes."
                          : "Let the avatar walk the learner through the document's slides on request, alongside answering from it."}
                      </p>
                    </div>
                    <Switch
                      id="tavus-presentation"
                      checked={presentationEnabled}
                      onCheckedChange={setPresentationEnabled}
                      disabled={presentationBlocked}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="tavus-internet-search">Internet search</Label>
                    <p className="text-xs text-muted-foreground">
                      {internetSearchProbe === "loading"
                        ? "Checking the current setting in Tavus…"
                        : internetSearchProbe === "error"
                          ? "Couldn't read the current setting from Tavus. Reopen the scenario to try again."
                          : "Let the avatar look up current information on the web during the session."}
                    </p>
                  </div>
                  <Switch
                    id="tavus-internet-search"
                    checked={internetSearchEnabled}
                    onCheckedChange={setInternetSearchEnabled}
                    disabled={internetSearchProbe === "loading" || internetSearchProbe === "error"}
                  />
                </div>
              </>
            )}
          </section>
        )}

        <section className="space-y-4 rounded-lg border bg-card p-4">
          <div>
            <h3 className="text-base font-semibold">Learner experience</h3>
            <p className="text-sm text-muted-foreground">
              Control how the session opens and what happens after it ends.
            </p>
          </div>
          <div
            ref={(n) => {
              fieldRefs.current.display = n;
            }}
          >
            <LearnerDisplayPicker
              value={display}
              onChange={(next) => {
                setDisplay(next);
                clearError("display");
              }}
              sources={sources}
              onSourcesChange={setSources}
              scenarioId={initial?.id}
              avatarThumbUrl={
                pickers.avatars.options.find((o) => o.id === avatarId)?.preview_url ??
                initial?.thumb_url
              }
            />
            <FieldError message={errors.display} />
          </div>
          {provider === "liveavatar" && (
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="force-byo-livekit">Always run on Powtoon LiveKit (BYO)</Label>
                <p className="text-xs text-muted-foreground">
                  Every session of this avatar, including public links, runs on Powtoon's
                  LiveKit. Only imported voices work there.
                </p>
              </div>
              <Switch id="force-byo-livekit" checked={forceByoLivekit} onCheckedChange={setForceByoLivekit} />
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="show-ai-notice">Show AI notice</Label>
              <p className="text-xs text-muted-foreground">
                Show learners a notice that they're talking to an AI, that answers may be
                inaccurate, not to share personal information, and that the conversation is
                transcribed.
              </p>
            </div>
            <Switch id="show-ai-notice" checked={showAiNotice} onCheckedChange={setShowAiNotice} />
          </div>
          {mode === "conversational" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="cta-sales">Book a meeting CTA</Label>
                  <p className="text-xs text-muted-foreground">
                    Show a split-screen booking panel alongside the avatar.
                  </p>
                </div>
                <Switch
                  id="cta-sales"
                  checked={ctaType === "sales"}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setCtaType("sales");
                    } else {
                      setCtaType("none");
                      setCtaUrl("");
                      clearError("ctaUrl");
                    }
                  }}
                />
              </div>
              {ctaType === "sales" && (
                <div className="space-y-1.5">
                  <Label htmlFor="cta-url">
                    Booking URL
                    <Req />
                  </Label>
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <Input
                        id="cta-url"
                        type="url"
                        ref={(el) => {
                          fieldRefs.current.ctaUrl = el;
                        }}
                        value={ctaUrl}
                        onChange={(e) => {
                          setCtaUrl(e.target.value);
                          clearError("ctaUrl");
                        }}
                        placeholder="https://calendly.com/..."
                        aria-invalid={!!errors.ctaUrl}
                        className={cn(errors.ctaUrl && errorRing)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setBookPreviewOpen(true)}
                      disabled={!/^https?:\/\/.+/i.test(ctaUrl.trim())}
                    >
                      Preview
                    </Button>
                  </div>
                  <FieldError message={errors.ctaUrl} />
                </div>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="redirect-url">Post-session redirect URL</Label>
            <Input
              id="redirect-url"
              type="url"
              ref={(el) => {
                fieldRefs.current.redirectUrl = el;
              }}
              value={redirectUrl}
              onChange={(e) => {
                setRedirectUrl(e.target.value);
                clearError("redirectUrl");
              }}
              placeholder="https://example.com/thank-you"
              aria-invalid={!!errors.redirectUrl}
              className={cn(errors.redirectUrl && errorRing)}
            />
            <FieldError message={errors.redirectUrl} />
            <p className="text-xs text-muted-foreground">
              Optional. Learners are sent here after the session ends.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="auto-start">Auto-start session</Label>
              <p className="text-xs text-muted-foreground">
                Skip the pre-session screen and go live immediately.
              </p>
            </div>
            <Switch id="auto-start" checked={autoStart} onCheckedChange={setAutoStart} />
          </div>
        </section>

        <section className="space-y-4 rounded-lg border bg-card p-4">
          <div className="space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold">
                  Description{publishMode !== "hidden" && <Req />}
                </h3>
                <p className="text-sm text-muted-foreground">
                  A short summary learners see when published into Powtoon workspace
                </p>
              </div>
              {(() => {
                const hasContext =
                  knowledge.trim().length > 0 ||
                  (mode === "role_play" && !isRolePlayConfigEmpty(roleplay));
                return (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleGenerateField("description")}
                    disabled={generatingDescription || !hasContext}
                    title={!hasContext ? "Add knowledge first" : undefined}
                    className="h-7 px-2.5 text-xs"
                  >
                    {generatingDescription ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3 mr-1" />
                    )}
                    {description.trim() ? "Enhance" : "Generate"}
                  </Button>
                );
              })()}
            </div>
            <Textarea
              id="description"
              ref={(el) => {
                fieldRefs.current.description = el;
              }}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                clearError("description");
              }}
              aria-invalid={!!errors.description}
              className={cn(errors.description && errorRing)}
              placeholder="Short description of what this scenario is for."
              disabled={generatingDescription}
            />
            <FieldError message={errors.description} />
          </div>
        </section>

        <div className="space-y-1.5">
          <Label>Workspace publish mode</Label>
          <div className="inline-flex rounded-md border bg-card p-0.5">
            {(["full", "demo", "upsell", "hidden"] as const).map((m) => {
              if (m === "upsell" && !allowUpsellPublishMode) {
                // Only render the "Upsell" chip when the current scenario is
                // already saved as upsell — as a disabled indicator — so orgs
                // without the flag don't see it in the picker at all.
                if (initial?.content?.publish_mode !== "upsell") return null;
                return (
                  <button
                    key={m}
                    type="button"
                    disabled
                    title="Only available for Powtoon workspaces"
                    className={
                      "px-3 py-1.5 text-sm rounded capitalize transition-colors cursor-not-allowed " +
                      (publishMode === m
                        ? "bg-primary/60 text-primary-foreground"
                        : "text-muted-foreground/60")
                    }
                  >
                    {m}
                  </button>
                );
              }
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setPublishMode(m);
                    if (m === "hidden") clearError("description");
                  }}
                  className={
                    "px-3 py-1.5 text-sm rounded capitalize transition-colors " +
                    (publishMode === m
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent")
                  }
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      </fieldset>

      <div className="flex items-center gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : submitLabel}
        </Button>
      </div>
      <AlertDialog
        open={pendingMode !== null}
        onOpenChange={(open) => {
          if (!open) setPendingMode(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch interaction mode?</AlertDialogTitle>
            <AlertDialogDescription>
              Switching mode will clear the {modeMeta.textareaLabel}, Persona, Instructions, and
              Source inputs because they are generated for the current mode. Name, description,
              opening text, avatar, and publish settings will be kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingMode) applyModeChange(pendingMode);
                setPendingMode(null);
              }}
            >
              Switch and clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={pendingProvider !== null}
        onOpenChange={(open) => {
          if (!open) setPendingProvider(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch provider?</AlertDialogTitle>
            <AlertDialogDescription>
              Switching provider clears the face / voice, and provider specific options. Persona,
              opening text and publish settings are kept.
              {(docEntries.length > 0 || didDocEntries.length > 0) && (
                <>
                  {" "}
                  Documents you registered with the current provider will be detached from this
                  scenario.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingProvider) applyProviderChange(pendingProvider);
                setPendingProvider(null);
              }}
            >
              Switch and clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BookMeetingPreviewModal
        open={bookPreviewOpen}
        onOpenChange={setBookPreviewOpen}
        bookingUrl={ctaUrl.trim()}
        avatarThumbUrl={
          pickers.avatars.options.find((o) => o.id === avatarId)?.preview_url ?? initial?.thumb_url
        }
        avatarName={pickers.avatars.options.find((o) => o.id === avatarId)?.label}
        avatarPosition={display?.avatarPosition ?? "right"}
      />
    </form>
  );
}
